import { useMemo, type ReactElement, type ReactNode } from 'react'
import { useWatch } from 'react-hook-form'
import { Box, Stack, Tooltip, useTheme } from '@mui/material'
import { EmptyState } from '@ui2/feedback'
import { MaterialSymbol } from '@ui2/icons'
import { useTransmissionStore } from '@editorStores'
import {
  useElementIdToAssembly,
  useElementLabel,
  useElementTypes,
  usePositionGuidToWallGuid,
} from '@editorHooks'
import { useSelectResults2Element } from '../../../hooks/useSelectResults2Element'
import { supportsGridLayoutSx } from '../constants'
import type { DesignFormRootValues } from '../types'
import { buildMemberCheckSettingSupportsContextIndex } from '../utils'
import SupportGeometrySceneMarkerEffect from './SupportGeometrySceneMarkerEffect'
import { SupportGridRow } from './SupportsGridRow'

const SupportsHeader = ({
  title,
  unit,
  hint,
}: {
  title: ReactNode
  unit: string
  hint?: string
}): ReactElement => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      lineHeight: 1.1,
    }}
  >
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <span>{title}</span>
      {hint ? (
        <Tooltip title={hint} arrow>
          <Box component="span" sx={{ display: 'inline-flex', color: 'text.secondary' }}>
            <MaterialSymbol icon="info" size={13} />
          </Box>
        </Tooltip>
      ) : null}
    </Box>
    <Box component="span" sx={{ fontSize: 9, color: 'rgba(0,0,0,0.38)' }}>
      {unit}
    </Box>
  </Box>
)

export const SupportsGrid = ({
  elementId,
  disableForm = false,
}: {
  elementId: string
  disableForm?: boolean
}): ReactElement => {
  const theme = useTheme()

  const rootPath = 'elements.0'
  const supportConfigsPath = `${rootPath}.memberCheckSettingsSelectedElement.support_configs`

  const watchedSupportConfigs = useWatch<DesignFormRootValues>({
    name: supportConfigsPath,
  }) as SupportConfig[] | null | undefined
  const supportConfigs = watchedSupportConfigs ?? []

  const elementTypesByGuid = useElementTypes()
  const selectSupportTargetElement = useSelectResults2Element(elementId)

  // These map over the whole model; resolve them once here and pass them down so
  // each SupportGridRow doesn't rebuild them (the form remounts on every element
  // selection, so per-row rebuilds dominate the selection latency).
  const getElementLabel = useElementLabel()
  const elementIdToAssembly = useElementIdToAssembly()
  const positionGuidToWallGuid = usePositionGuidToWallGuid()

  const supportConfigIndexToElementIndex = useWatch<DesignFormRootValues>({
    name: 'supportConfigIndexToElementIndex',
  }) as DesignFormRootValues['supportConfigIndexToElementIndex']

  const rootElementType = elementTypesByGuid[elementId] as ElementTypes
  const graph = useTransmissionStore(state => state.verticalTransmissionGraph)
  const supportsContextIndex = useMemo(
    () => buildMemberCheckSettingSupportsContextIndex(graph),
    [graph],
  )

  return (
    <Box sx={{ pt: 1.25 }}>
      <SupportGeometrySceneMarkerEffect elementId={elementId} />
      <Box
        sx={{
          ...supportsGridLayoutSx,
          fontSize: 12,
          color: 'text.secondary',
          pb: 0.5,
        }}
      >
        <Box />
        <Box>Bauteil</Box>
        <SupportsHeader
          title={
            <>
              F<Box component="sub">d</Box>
            </>
          }
          unit="[kN]"
        />
        <SupportsHeader title="QS" unit="[cm]" />
        <Stack direction="column" alignItems="center" gap={0.25} sx={{ lineHeight: 1.1 }}>
          <Box component="span">
            η<Box component="sub">c,90</Box>
          </Box>
        </Stack>
        <SupportsHeader title="G. Auflager" unit="[cm]" />
        <SupportsHeader
          title="Überstand"
          unit="[cm]"
          hint="l = links, r = rechts, v = vorne, h = hinten"
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <span>
              k<Box component="sub">c,90</Box>
            </span>
            <Tooltip title="Beiwert zur Berücksichtigung der Querdruckverteilung" arrow>
              <Box component="span" sx={{ display: 'inline-flex', color: 'text.secondary' }}>
                <MaterialSymbol icon="info" size={13} />
              </Box>
            </Tooltip>
          </Box>
        </Box>
        <SupportsHeader
          title={
            <>
              f<Box component="sub">c,90,k</Box>
            </>
          }
          unit="[N/mm²]"
        />
      </Box>

      {supportConfigs.length === 0 ? (
        <EmptyState sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
          Keine Auflagereinstellungen verfügbar
        </EmptyState>
      ) : null}

      {supportConfigs.map((config, index) => (
        <SupportGridRow
          key={index}
          supportConfig={config}
          onSelectSupportTarget={selectSupportTargetElement}
          elementIndex={supportConfigIndexToElementIndex[index]}
          rowIndex={index}
          disableForm={disableForm}
          rootElementGuid={elementId}
          rootElementType={rootElementType}
          supportsContextIndex={supportsContextIndex}
          getElementLabel={getElementLabel}
          elementTypesByGuid={elementTypesByGuid}
          elementIdToAssembly={elementIdToAssembly}
          positionGuidToWallGuid={positionGuidToWallGuid}
        />
      ))}
    </Box>
  )
}
